import React, { useState, useRef } from "react";
import { HbrSpinner } from "@harbor/elements-react-standalone";
import StbButton from "../security-toolbox-ui/StbButton";
import { downloadSimple } from "@harbor/elements/icons";
import ReactMarkdown from "react-markdown";

interface PdfExportProps {
  /** Content to export - can be HTML string or markdown */
  content: string | React.ReactNode;
  /** Filename for the PDF (without .pdf extension) */
  filename?: string;
  /** Title to display in the PDF */
  title?: string;
  /** Additional metadata */
  metadata?: {
    author?: string;
    subject?: string;
    keywords?: string;
  };
  /** Custom button label */
  buttonLabel?: string;
  /** Button icon */
  buttonIcon?: any;
  /** Custom CSS styles to apply to the PDF content */
  customStyles?: string;
  /** Callback after successful export */
  onExportComplete?: () => void;
  /** Callback on export error */
  onExportError?: (error: Error) => void;
  /** Include RAG sources in export */
  ragChunks?: any[];
  /** Custom button class */
  className?: string;
  /** Is content markdown that needs rendering? */
  isMarkdown?: boolean;
}

const PdfExport: React.FC<PdfExportProps> = ({
                                               content,
                                               filename = "export",
                                               title,
                                               metadata = {},
                                               buttonLabel = "Export PDF",
                                               buttonIcon = downloadSimple,
                                               customStyles = "",
                                               onExportComplete,
                                               onExportError,
                                               ragChunks = [],
                                               className = "",
                                               isMarkdown = true,
                                             }) => {
  const [isExporting, setIsExporting] = useState(false);
  const hiddenContentRef = useRef<HTMLDivElement>(null);

  /**
   * Default styles for PDF content
   */
  const getDefaultStyles = (): string => {
    return `
      <style>
        @page {
          size: A4;
          margin: 2cm;
        }
        
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          font-size: 12pt;
          line-height: 1.6;
          color: #333;
          max-width: 100%;
        }
        
        h1 {
          font-size: 24pt;
          font-weight: bold;
          margin-top: 0;
          margin-bottom: 1em;
          color: #1a1a1a;
          border-bottom: 2px solid #333;
          padding-bottom: 0.5em;
        }
        
        h2 {
          font-size: 18pt;
          font-weight: 600;
          margin-top: 1.5em;
          margin-bottom: 0.75em;
          color: #2c2c2c;
        }
        
        h3 {
          font-size: 14pt;
          font-weight: 600;
          margin-top: 1.25em;
          margin-bottom: 0.5em;
          color: #3c3c3c;
        }
        
        p {
          margin: 0.75em 0;
          text-align: justify;
        }
        
        ul, ol {
          margin: 0.75em 0;
          padding-left: 2em;
        }
        
        li {
          margin: 0.5em 0;
        }
        
        code {
          background-color: #f4f4f4;
          padding: 2px 6px;
          border-radius: 3px;
          font-family: 'Courier New', Courier, monospace;
          font-size: 11pt;
        }
        
        pre {
          background-color: #f4f4f4;
          padding: 1em;
          border-radius: 5px;
          overflow-x: auto;
          margin: 1em 0;
          font-family: 'Courier New', Courier, monospace;
          font-size: 10pt;
          line-height: 1.4;
        }
        
        pre code {
          background-color: transparent;
          padding: 0;
        }
        
        a {
          color: #0066cc;
          text-decoration: underline;
        }
        
        blockquote {
          border-left: 4px solid #ccc;
          padding-left: 1em;
          margin-left: 0;
          color: #666;
          font-style: italic;
        }
        
        table {
          border-collapse: collapse;
          width: 100%;
          margin: 1em 0;
        }
        
        th, td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: left;
        }
        
        th {
          background-color: #f4f4f4;
          font-weight: bold;
        }
        
        .metadata {
          font-size: 10pt;
          color: #666;
          margin-bottom: 2em;
          padding-bottom: 1em;
          border-bottom: 1px solid #ddd;
        }
        
        .rag-sources {
          margin-top: 3em;
          page-break-before: always;
        }
        
        .rag-chunk {
          border: 1px solid #ddd;
          padding: 1em;
          margin: 1em 0;
          border-radius: 5px;
          background-color: #fafafa;
          page-break-inside: avoid;
        }
        
        .rag-chunk-header {
          font-weight: 600;
          margin-bottom: 0.5em;
          color: #2c2c2c;
        }
        
        .rag-chunk-content {
          font-size: 10pt;
          white-space: pre-wrap;
          word-wrap: break-word;
        }
        
        .footer {
          margin-top: 2em;
          padding-top: 1em;
          border-top: 1px solid #ddd;
          font-size: 10pt;
          color: #666;
          text-align: center;
        }
        
        /* Ensure proper list styling */
        .list-disc {
          list-style-type: disc;
        }
        
        .list-decimal {
          list-style-type: decimal;
        }
        
        ${customStyles}
      </style>
    `;
  };

  /**
   * Generate metadata section HTML
   */
  const generateMetadataHtml = (): string => {
    const metadataItems: string[] = [];
    const now = new Date().toLocaleString();

    metadataItems.push(`<strong>Generated:</strong> ${now}`);

    if (metadata.author) {
      metadataItems.push(`<strong>Author:</strong> ${metadata.author}`);
    }
    if (metadata.subject) {
      metadataItems.push(`<strong>Subject:</strong> ${metadata.subject}`);
    }
    if (metadata.keywords) {
      metadataItems.push(`<strong>Keywords:</strong> ${metadata.keywords}`);
    }

    return metadataItems.length > 0
      ? `<div class="metadata">${metadataItems.join(" | ")}</div>`
      : "";
  };

  /**
   * Generate RAG sources HTML
   */
  const generateRagHtml = (): string => {
    if (!ragChunks || ragChunks.length === 0) {
      return "";
    }

    const chunksHtml = ragChunks
      .slice(0, 10)
      .map((chunk: any, idx: number) => {
        const source = chunk.source || chunk.metadata?.source || `Source ${idx + 1}`;
        const score = chunk.score != null ? ` (Relevance: ${chunk.score.toFixed(3)})` : "";
        const text = (chunk.text || "").slice(0, 1000);
        const truncated = (chunk.text || "").length > 1000 ? "..." : "";

        return `
          <div class="rag-chunk">
            <div class="rag-chunk-header">${source}${score}</div>
            <div class="rag-chunk-content">${text}${truncated}</div>
          </div>
        `;
      })
      .join("");

    return `
      <div class="rag-sources">
        <h2>Reference Sources</h2>
        ${chunksHtml}
      </div>
    `;
  };

  /**
   * Extract rendered HTML from the hidden content div
   */
  const getRenderedHtml = (): string => {
    if (hiddenContentRef.current) {
      return hiddenContentRef.current.innerHTML;
    }
    return typeof content === "string" ? content : String(content);
  };

  /**
   * Sanitize filename to ensure it's valid
   */
  const sanitizeFilename = (name: string): string => {
    return name
      .replace(/[^a-z0-9_-]/gi, '_')
      .replace(/_{2,}/g, '_')
      .substring(0, 200); // Limit length
  };

  /**
   * Generate complete HTML document with proper title
   */
  const generateHtmlDocument = (): string => {
    const htmlContent = getRenderedHtml();
    const styles = getDefaultStyles();
    const metadataHtml = generateMetadataHtml();
    const ragHtml = generateRagHtml();
    const titleHtml = title ? `<h1>${title}</h1>` : "";

    // Use filename for document title to suggest it in save dialog
    const documentTitle = `${filename}.pdf`;

    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${documentTitle}</title>
        ${styles}
      </head>
      <body>
        ${titleHtml}
        ${htmlContent}
        ${ragHtml}
        <div class="footer">
          Generated on ${new Date().toLocaleString()}
        </div>
      </body>
      </html>
    `;
  };

  /**
   * Export to PDF using browser's print functionality
   */
  const exportToPdf = async () => {
    setIsExporting(true);

    // Store original page title to restore later. Declared outside the try so
    // the error path can restore it too - otherwise a throw after the title is
    // changed leaves the tab permanently reading "<filename>.pdf".
    const originalTitle = document.title;

    const restoreTitleAndCleanup = () => {
      document.title = originalTitle;
      const iframeElement = document.getElementById("pdf-export-iframe");
      if (iframeElement) {
        document.body.removeChild(iframeElement);
      }
    };

    try {
      const htmlDocument = generateHtmlDocument();
      const sanitizedFilename = sanitizeFilename(filename);

      // Temporarily change the main page title (helps some browsers)
      document.title = `${sanitizedFilename}.pdf`;

      // Create a hidden iframe
      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      iframe.setAttribute("id", "pdf-export-iframe");
      document.body.appendChild(iframe);

      // Write content to iframe
      const iframeDoc = iframe.contentWindow?.document;
      if (!iframeDoc) {
        throw new Error("Failed to access iframe document");
      }

      iframeDoc.open();
      iframeDoc.write(htmlDocument);
      iframeDoc.close();

      // Wait for content to load and images to render
      await new Promise((resolve) => {
        if (iframe.contentWindow) {
          iframe.contentWindow.addEventListener("load", resolve);
        }
        setTimeout(resolve, 1500); // Fallback timeout
      });

      // Set the iframe document title explicitly
      if (iframeDoc) {
        iframeDoc.title = `${sanitizedFilename}.pdf`;
      }

      // Focus the iframe and trigger print
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();

      // Restore original title and cleanup
      setTimeout(() => {
        restoreTitleAndCleanup();
        setIsExporting(false);
        onExportComplete?.();
      }, 1000);

    } catch (error) {
      console.error("PDF export error:", error);
      restoreTitleAndCleanup();
      setIsExporting(false);
      onExportError?.(error as Error);
    }
  };

  return (
    <>
      {/* Hidden rendered content for extraction */}
      {isMarkdown && typeof content === "string" && (
        <div
          ref={hiddenContentRef}
          style={{
            position: "absolute",
            left: "-9999px",
            top: "-9999px",
            width: "800px",
            visibility: "hidden",
          }}
        >
          <ReactMarkdown
            components={{
              h2: ({ node, ...props }) => (
                <h2 style={{ fontSize: "18pt", fontWeight: 600, marginTop: "1.5em", marginBottom: "0.75em" }} {...props} />
              ),
              h3: ({ node, ...props }) => (
                <h3 style={{ fontSize: "14pt", fontWeight: 600, marginTop: "1.25em", marginBottom: "0.5em" }} {...props} />
              ),
              ul: ({ node, ordered, depth, ...props }: any) => (
                <ul className="list-disc" style={{ paddingLeft: "2em", margin: "0.75em 0" }} {...props} />
              ),
              ol: ({ node, ordered, depth, ...props }: any) => (
                <ol className="list-decimal" style={{ paddingLeft: "2em", margin: "0.75em 0" }} {...props} />
              ),
              li: ({ node, ordered, checked, index, depth, ...props }: any) => (
                <li style={{ margin: "0.5em 0" }} {...props} />
              ),
              p: ({ node, ...props }) => <p style={{ margin: "0.75em 0" }} {...props} />,
              a: ({ node, ...props }) => (
                <a style={{ color: "#0066cc", textDecoration: "underline" }} target="_blank" rel="noreferrer" {...props} />
              ),
              code: ({ node, ...props }) => (
                <code
                  style={{
                    backgroundColor: "#f4f4f4",
                    padding: "2px 6px",
                    borderRadius: "3px",
                    fontFamily: "Courier New, monospace",
                  }}
                  {...props}
                />
              ),
              pre: ({ node, ...props }) => (
                <pre
                  style={{
                    backgroundColor: "#f4f4f4",
                    padding: "1em",
                    borderRadius: "5px",
                    overflow: "auto",
                    margin: "1em 0",
                  }}
                  {...props}
                />
              ),
            }}
          >
            {content as string}
          </ReactMarkdown>
        </div>
      )}

      {/* Export button */}
      <div className={className}>
        <StbButton onClick={exportToPdf} icon={buttonIcon} disabled={isExporting} className="relative">
          {isExporting ? (
            <span className="flex items-center gap-2">
              <HbrSpinner size="small" />
              Exporting...
            </span>
          ) : (
            buttonLabel
          )}
        </StbButton>
      </div>
    </>
  );
};

export default PdfExport;